import { createContext, useContext, useState, ReactNode, useEffect } from "react";

type Language = "de" | "en" | "fr" | "nl" | "it" | "es";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  isUSDomain: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Check if we're on the US domain
const checkIsUSDomain = (): boolean => {
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname.toLowerCase();
    return hostname.includes("eap-abutments.us") || hostname.includes(".us");
  }
  return false;
};

const getInitialLanguage = (): Language => {
  if (typeof window !== "undefined") {
    // For US domain, default to English unless user explicitly changed it
    const isUS = checkIsUSDomain();
    const saved = localStorage.getItem("eap-language");
    
    if (saved && ["de", "en", "fr", "nl", "it", "es"].includes(saved)) {
      return saved as Language;
    }
    
    // Default: English for US domain, German otherwise
    return isUS ? "en" : "de";
  }
  return "de";
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<Language>(getInitialLanguage);
  const [isUSDomain] = useState<boolean>(checkIsUSDomain);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem("eap-language", lang);
  };

  useEffect(() => {
    // Sync with localStorage on mount, but respect US domain default
    const saved = localStorage.getItem("eap-language");
    if (saved && ["de", "en", "fr", "nl", "it", "es"].includes(saved)) {
      setLanguageState(saved as Language);
    } else if (isUSDomain) {
      // No saved preference on US domain → set English
      setLanguageState("en");
    }
  }, [isUSDomain]);

  // Keep <html lang="..."> in sync with the selected language (a11y + SEO)
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = language;
    }
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, isUSDomain }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
};
